import {
  IAdminPanelApiCall,
  IRestApiService,
  AdminPanelListUsersParams,
  AdminPanelUsersResponse,
  AdminPanelSubscriptionDetails,
  AdminPanelInvoicesResponse,
  AdminPanelCustomInvoiceResult,
  AdminPanelDiscountInput,
  AdminPanelLowCostInput,
  AdminPanelReportJob,
  AdminPanelSubscriptionAnalytics,
  AdminPanelAtRiskAccount,
  AdminPanelAgendaJob,
  AdminPanelAgendaTriggerLogEntry,
  AdminPanelAddonCatalogEntry,
  AdminPanelSubscriptionAddon,
  AdminPanelUpdateAddonsResult,
  AdminPanelEmployeeRow,
  AdminPanelBotsResetStatus,
  AdminPanelPlatformPause,
  AdminPanelSystemHealth,
} from '../types';

class AdminPanelApiCall implements IAdminPanelApiCall {
  private restApiService: IRestApiService;

  constructor(restApiService: IRestApiService) {
    this.restApiService = restApiService;
  }

  getUsers(params: AdminPanelListUsersParams): Promise<AdminPanelUsersResponse> {
    const query = new URLSearchParams({
      page: String(params.page),
      size: String(params.size),
      sortBy: params.sortBy || 'nextPaymentDate',
      sortDir: params.sortDir || 'asc',
    });
    if (params.statuses?.length) {
      query.set('statuses', params.statuses.join(','));
    }
    if (params.search?.trim()) {
      query.set('search', params.search.trim());
    }
    // Only sent when on: the server treats anything other than the literal "true" as "no filter",
    // so an explicit lowCost=false would just be noise in the URL.
    if (params.lowCost) {
      query.set('lowCost', 'true');
    }
    return this.restApiService.get<AdminPanelUsersResponse>(`admin-panel/users?${query.toString()}`);
  }

  deleteUser(ownerId: string): Promise<{ deleted: true }> {
    return this.restApiService.delete<{ deleted: true }>(`admin-panel/owner/${ownerId}`);
  }

  getStatuses(): Promise<string[]> {
    return this.restApiService.get<string[]>('admin-panel/statuses');
  }

  getSubscriptionDetails(ownerId: string): Promise<AdminPanelSubscriptionDetails> {
    return this.restApiService.get<AdminPanelSubscriptionDetails>(`admin-panel/owner/${ownerId}/subscription`);
  }

  updateSubscription(ownerId: string, data: Record<string, any>): Promise<AdminPanelSubscriptionDetails> {
    return this.restApiService.put<AdminPanelSubscriptionDetails>(`admin-panel/owner/${ownerId}/subscription`, data);
  }

  getInvoices(ownerId: string, page: number, size: number): Promise<AdminPanelInvoicesResponse> {
    return this.restApiService.get<AdminPanelInvoicesResponse>(
      `admin-panel/owner/${ownerId}/invoices?page=${page}&size=${size}`
    );
  }

  getEmployees(ownerId: string): Promise<AdminPanelEmployeeRow[]> {
    return this.restApiService.get<AdminPanelEmployeeRow[]>(`admin-panel/owner/${ownerId}/employees`);
  }

  createCustomInvoice(
    ownerId: string,
    data: { amount: number; purpose: string }
  ): Promise<AdminPanelCustomInvoiceResult> {
    return this.restApiService.post<AdminPanelCustomInvoiceResult>(`admin-panel/owner/${ownerId}/custom-invoice`, data);
  }

  createSubscriptionInvoice(ownerId: string): Promise<AdminPanelCustomInvoiceResult> {
    return this.restApiService.post<AdminPanelCustomInvoiceResult>(`admin-panel/owner/${ownerId}/subscription-invoice`);
  }

  deleteInvoice(ownerId: string, invoiceId: string): Promise<{ deleted: true }> {
    return this.restApiService.delete<{ deleted: true }>(`admin-panel/owner/${ownerId}/invoices/${invoiceId}`);
  }

  addDiscount(ownerId: string, data: AdminPanelDiscountInput): Promise<AdminPanelSubscriptionDetails> {
    return this.restApiService.post<AdminPanelSubscriptionDetails>(`admin-panel/owner/${ownerId}/discount`, data);
  }

  removeDiscount(ownerId: string): Promise<AdminPanelSubscriptionDetails> {
    return this.restApiService.delete<AdminPanelSubscriptionDetails>(`admin-panel/owner/${ownerId}/discount`);
  }

  setLowCost(ownerId: string, data: AdminPanelLowCostInput): Promise<AdminPanelSubscriptionDetails> {
    return this.restApiService.post<AdminPanelSubscriptionDetails>(`admin-panel/owner/${ownerId}/low-cost`, data);
  }

  extendTrial(ownerId: string, days: number): Promise<AdminPanelSubscriptionDetails> {
    return this.restApiService.post<AdminPanelSubscriptionDetails>(`admin-panel/owner/${ownerId}/extend-trial`, {
      days,
    });
  }

  extendDeletion(ownerId: string, days: number): Promise<AdminPanelSubscriptionDetails> {
    return this.restApiService.post<AdminPanelSubscriptionDetails>(`admin-panel/owner/${ownerId}/extend-deletion`, {
      days,
    });
  }

  cancelSubscription(ownerId: string): Promise<AdminPanelSubscriptionDetails> {
    return this.restApiService.post<AdminPanelSubscriptionDetails>(`admin-panel/owner/${ownerId}/cancel`);
  }

  resumeSubscription(ownerId: string): Promise<AdminPanelSubscriptionDetails> {
    return this.restApiService.post<AdminPanelSubscriptionDetails>(`admin-panel/owner/${ownerId}/resume`);
  }

  blockSubscription(ownerId: string): Promise<AdminPanelSubscriptionDetails> {
    return this.restApiService.post<AdminPanelSubscriptionDetails>(`admin-panel/owner/${ownerId}/block`);
  }

  setDeletionProtection(ownerId: string, enabled: boolean): Promise<AdminPanelSubscriptionDetails> {
    return this.restApiService.post<AdminPanelSubscriptionDetails>(`admin-panel/owner/${ownerId}/deletion-protection`, {
      enabled,
    });
  }

  activateLowCostPayment(ownerId: string, nextPaymentDate: string): Promise<AdminPanelSubscriptionDetails> {
    return this.restApiService.post<AdminPanelSubscriptionDetails>(
      `admin-panel/owner/${ownerId}/low-cost/activate-payment`,
      { nextPaymentDate }
    );
  }

  changeBlockDate(ownerId: string, willBlockDate: string): Promise<AdminPanelSubscriptionDetails> {
    return this.restApiService.post<AdminPanelSubscriptionDetails>(`admin-panel/owner/${ownerId}/block-date`, {
      willBlockDate,
    });
  }

  changeNextPaymentDate(ownerId: string, nextPaymentDate: string): Promise<AdminPanelSubscriptionDetails> {
    return this.restApiService.post<AdminPanelSubscriptionDetails>(`admin-panel/owner/${ownerId}/next-payment-date`, {
      nextPaymentDate,
    });
  }

  changeStatus(
    ownerId: string,
    status: string,
    willBlockDate?: string,
    trialEndDate?: string
  ): Promise<AdminPanelSubscriptionDetails> {
    return this.restApiService.post<AdminPanelSubscriptionDetails>(`admin-panel/owner/${ownerId}/status`, {
      status,
      willBlockDate,
      trialEndDate,
    });
  }

  getAddonCatalog(): Promise<AdminPanelAddonCatalogEntry[]> {
    return this.restApiService.get<AdminPanelAddonCatalogEntry[]>('admin-panel/addon-catalog');
  }

  updateAddons(
    ownerId: string,
    subscriptionData: { tariff: { label: string; billingPeriod: string }; addons: AdminPanelSubscriptionAddon[] }
  ): Promise<AdminPanelUpdateAddonsResult> {
    return this.restApiService.put<AdminPanelUpdateAddonsResult>(
      `admin-panel/owner/${ownerId}/addons`,
      subscriptionData
    );
  }

  removeAddon(ownerId: string, addonLabel: string): Promise<AdminPanelSubscriptionDetails> {
    return this.restApiService.delete<AdminPanelSubscriptionDetails>(
      `admin-panel/owner/${ownerId}/addons/${addonLabel}`
    );
  }

  setAddonFree(ownerId: string, addonLabel: string, free: boolean): Promise<AdminPanelSubscriptionDetails> {
    return this.restApiService.post<AdminPanelSubscriptionDetails>(
      `admin-panel/owner/${ownerId}/addons/${addonLabel}/free`,
      { free }
    );
  }

  deletePaymentSource(ownerId: string, source: string): Promise<{ deleted: true }> {
    // Sources are a free-text array (a manager can add a custom name via updatePaymentSettings,
    // not just "CASH"/"CARD"), so this must be URL-encoded - a name with a space or "/" would
    // otherwise break the route match server-side.
    return this.restApiService.delete<{ deleted: true }>(
      `admin-panel/owner/${ownerId}/payment-sources/${encodeURIComponent(source)}`
    );
  }

  resetOwnerPassword(ownerId: string): Promise<{ sent: true }> {
    return this.restApiService.post<{ sent: true }>(`admin-panel/owner/${ownerId}/reset-password`);
  }

  startUniqueClientsReport(dateFrom: string, dateTo: string, statuses: string[]): Promise<AdminPanelReportJob> {
    const query = new URLSearchParams({ dateFrom, dateTo, statuses: statuses.join(',') });
    return this.restApiService.get<AdminPanelReportJob>(`admin-panel/reports/unique-clients?${query.toString()}`);
  }

  exportUsers(params: Omit<AdminPanelListUsersParams, 'page' | 'size'>): Promise<Blob> {
    const query = new URLSearchParams({
      sortBy: params.sortBy || 'nextPaymentDate',
      sortDir: params.sortDir || 'asc',
    });
    if (params.statuses?.length) {
      query.set('statuses', params.statuses.join(','));
    }
    if (params.search?.trim()) {
      query.set('search', params.search.trim());
    }
    if (params.lowCost) {
      query.set('lowCost', 'true');
    }
    return this.restApiService.doRequest<Blob>(
      `admin-panel/users/export?${query.toString()}`,
      'GET',
      undefined,
      undefined,
      {
        responseType: 'blob',
      }
    );
  }

  startUserDataExport(ownerId: string): Promise<AdminPanelReportJob> {
    return this.restApiService.post<AdminPanelReportJob>(`admin-panel/owner/${ownerId}/export`);
  }

  getAgendaJobs(): Promise<AdminPanelAgendaJob[]> {
    return this.restApiService.get<AdminPanelAgendaJob[]>('admin-panel/agenda/jobs');
  }

  getAgendaThrottleStatus(): Promise<{ retryAfterSeconds: number }> {
    return this.restApiService.get<{ retryAfterSeconds: number }>('admin-panel/agenda/throttle-status');
  }

  getAgendaTriggerLog(): Promise<AdminPanelAgendaTriggerLogEntry[]> {
    return this.restApiService.get<AdminPanelAgendaTriggerLogEntry[]>('admin-panel/agenda/trigger-log');
  }

  runAgendaJob(jobName: string): Promise<{ triggered: true }> {
    return this.restApiService.post<{ triggered: true }>(`admin-panel/agenda/jobs/${jobName}/run`);
  }

  startBotsReset(): Promise<AdminPanelBotsResetStatus> {
    return this.restApiService.post<AdminPanelBotsResetStatus>('admin-panel/tools/reset-bots');
  }

  getBotsResetStatus(): Promise<AdminPanelBotsResetStatus> {
    return this.restApiService.get<AdminPanelBotsResetStatus>('admin-panel/tools/reset-bots');
  }

  getSystemHealth(): Promise<AdminPanelSystemHealth> {
    return this.restApiService.get<AdminPanelSystemHealth>('admin-panel/tools/health');
  }

  getPlatformPause(): Promise<AdminPanelPlatformPause> {
    return this.restApiService.get<AdminPanelPlatformPause>('admin-panel/platform-pause');
  }

  setPlatformPause(enabled: boolean, message?: string): Promise<AdminPanelPlatformPause> {
    return this.restApiService.post<AdminPanelPlatformPause>('admin-panel/platform-pause', { enabled, message });
  }

  getSubscriptionAnalytics(monthsBack?: number): Promise<AdminPanelSubscriptionAnalytics> {
    const query = monthsBack ? `?monthsBack=${monthsBack}` : '';
    return this.restApiService.get<AdminPanelSubscriptionAnalytics>(`admin-panel/analytics/subscriptions${query}`);
  }

  getAtRiskAccounts(limit?: number): Promise<AdminPanelAtRiskAccount[]> {
    const query = limit ? `?limit=${limit}` : '';
    return this.restApiService.get<AdminPanelAtRiskAccount[]>(`admin-panel/analytics/at-risk${query}`);
  }
}

export default AdminPanelApiCall;
